"use client";

import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState } from "react";
import { type DateRange } from "react-day-picker";

import { Button } from "@/src/components/shadcn/ui/button";
import { Calendar, type CalendarProps } from "@/src/components/shadcn/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { cn } from "@/src/lib/shadcn-utils";

interface DatePickerProps extends Omit<CalendarProps, "mode" | "selected" | "onSelect"> {
  className?: string;
  placeholder?: string;
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  closeOnSelect?: boolean;
}

function DatePicker({
  className,
  placeholder = "Select a date",
  date,
  onDateChange,
  closeOnSelect = false,
  ...calendarProps
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const handleDateChange = (selectedDate: Date | undefined) => {
    onDateChange(selectedDate);

    if (closeOnSelect && selectedDate) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal min-w-0 overflow-hidden",
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="truncate">{date ? format(date, "PPP") : <span>{placeholder}</span>}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={handleDateChange} autoFocus {...calendarProps} />
      </PopoverContent>
    </Popover>
  );
}

interface DateRangePickerProps extends Omit<CalendarProps, "mode" | "selected" | "onSelect"> {
  className?: string;
  placeholder?: string;
  dateRange: DateRange | undefined;
  onDateRangeChange: (dateRange: DateRange | undefined) => void;
}

function DateRangePicker({
  className,
  placeholder = "Select a date",
  dateRange,
  onDateRangeChange,
  ...calendarProps
}: DateRangePickerProps) {
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "justify-start text-left font-normal min-w-0 overflow-hidden",
              !dateRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(dateRange.from, "LLL dd, y")
                )
              ) : (
                <span>{placeholder}</span>
              )}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={onDateRangeChange}
            numberOfMonths={2}
            {...calendarProps}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { DatePicker, DateRangePicker };
